import React, { useRef, useEffect } from 'react';
import { Send, X } from 'lucide-react';
import { FormField } from '../utils/chatUtils';

interface ChatInputProps {
  currentMessage: string;
  setCurrentMessage: (message: string) => void;
  handleSendMessage: () => void;
  handleKeyPress: (e: React.KeyboardEvent) => void;
  isContactFormMode: boolean;
  contactFormStep: FormField | 'confirmation' | null;
  exitContactForm: () => void;
  isSubmitting: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
  currentMessage,
  setCurrentMessage,
  handleSendMessage,
  handleKeyPress,
  isContactFormMode,
  contactFormStep,
  exitContactForm,
  isSubmitting
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  let placeholderText = 'Digite sua mensagem...';
  
  if (isContactFormMode) {
    switch (contactFormStep) {
      case 'name':
        placeholderText = 'Digite seu nome completo...';
        break;
      case 'email':
        placeholderText = 'Digite seu email...';
        break;
      case 'subject':
        placeholderText = 'Digite o assunto...';
        break;
      case 'message':
        placeholderText = 'Digite sua mensagem...';
        break;
      case 'confirmation':
        placeholderText = 'Digite "sim" ou "não"...';
        break;
    }
  }
  
  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to calculate properly
      textareaRef.current.style.height = 'auto';
      
      // Set new height based on scrollHeight (with a max height)
      const newHeight = Math.min(textareaRef.current.scrollHeight, 120);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [currentMessage]);
  
  // Focus textarea when component mounts
  useEffect(() => {
    if (textareaRef.current && !isSubmitting) {
      textareaRef.current.focus();
    }
  }, [isSubmitting, contactFormStep]);

  return (
    <div className="input-area-container">
      {isContactFormMode && contactFormStep === 'confirmation' && (
        <div className="helper-text">
          Digite "sim" para confirmar ou "não" para cancelar
        </div>
      )}
      <div className="px-4 py-3 flex items-center gap-3 input-area">
        {isContactFormMode && (
          <button
            onClick={exitContactForm}
            className="cancel-button"
            aria-label="Cancelar formulário"
            disabled={isSubmitting}
            type="button"
          >
            <X size={20} />
          </button>
        )}
        
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={currentMessage}
            onChange={(e) => setCurrentMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={placeholderText}
            className={`message-input ${isContactFormMode && contactFormStep === 'confirmation' ? 'confirmation-input' : ''}`}
            rows={1}
            disabled={isSubmitting}
            aria-label="Mensagem"
          />
        </div>
        
        <button
          onClick={handleSendMessage}
          className="send-button"
          disabled={currentMessage.trim() === '' || isSubmitting}
          aria-label="Enviar mensagem"
          type="button"
        >
          <Send size={18} />
        </button>
      </div>
    </div>
  );
};

export default ChatInput;
